import React, { useState } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    TextInput,
    Alert,
    ActivityIndicator,
    KeyboardAvoidingView,
    Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { PlanService, UserService } from '../../services/ApiService';
import PlanGenerationScreen from './PlanGenerationScreen';

const TentativePlanScreen = ({ route, navigation, onOnboardingComplete }) => {
    const insets = useSafeAreaInsets();
    const initialPlan = route.params?.planData;
    const initialJobId = route.params?.jobId;
    const planType = route.params?.planType || 'basic';
    const onboardingData = route.params?.onboardingData;

    const [plan, setPlan] = useState(initialPlan);
    const [refinementText, setRefinementText] = useState('');
    const [isRefining, setIsRefining] = useState(false);
    const [refiningJobId, setRefiningJobId] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        try {
            setLoading(true);

            // Activate the currently reviewed plan version in the DB.
            // This ensures DB "active" status only becomes true after the user confirms.
            const planVersionId = plan?._id || plan?.planVersionId;
            if (!planVersionId) {
                throw new Error('Missing plan version id for activation.');
            }
            await PlanService.activatePlanVersion(planVersionId);

            if (onOnboardingComplete) {
                await onOnboardingComplete();
            } else {
                // Fallback if prop not passed, though App.js should pass it
                navigation.navigate('Main');
            }
        } catch (error) {
            console.error('Error confirming plan:', error);
            Alert.alert('Error', 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestChanges = async () => {
        if (!refinementText.trim()) {
            Alert.alert('Please enter your feedback', 'Tell us what you would like to change about this plan.');
            return;
        }

        try {
            setLoading(true);

            // Prepare updated onboarding data with user feedback
            let updatedOnboardingData = { ...onboardingData, isTentativePlan: true };

            // Append feedback to existing additional context or create new
            if (updatedOnboardingData.additionalContext) {
                updatedOnboardingData.additionalContext += `\n\nUser Revision Request: ${refinementText}`;
            } else {
                updatedOnboardingData.additionalContext = `User Revision Request: ${refinementText}`;
            }

            console.log('Requesting plan revision via completeOnboarding...');
            const response = await UserService.completeOnboarding(updatedOnboardingData);
            console.log('Revision request response:', response);

            if (response.success && response.data && response.data.jobId) {
                setRefiningJobId(response.data.jobId);
                setIsRefining(true);
            } else {
                console.warn('Unexpected response format:', response);
                Alert.alert('Error', 'Failed to start plan regeneration. Please try again.');
            }

        } catch (error) {
            console.error('Error requesting changes:', error);
            Alert.alert('Error', 'Failed to submit changes. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRefinementComplete = (newPlan) => {
        console.log('Plan refined:', newPlan);
        setPlan(newPlan);
        setIsRefining(false);
        setRefiningJobId(null);
        setRefinementText(''); // Clear input
        Alert.alert('Success', 'Your plan has been updated based on your feedback!');
    };

    const handleRefinementError = (error) => {
        console.error('Refinement failed:', error);
        setIsRefining(false);
        setRefiningJobId(null);
        Alert.alert('Error', 'Failed to update plan. Please try again.');
    };

    if (isRefining && refiningJobId) {
        return (
            <PlanGenerationScreen
                jobId={refiningJobId}
                planType={planType}
                onComplete={handleRefinementComplete}
                onError={handleRefinementError}
            />
        );
    }

    // Helper to render plan summary with real data
    const renderPlanSummary = () => {
        if (!plan) return <Text style={styles.placeholderText}>No plan data available.</Text>;

        // Handle both camelCase (internal app model) and snake_case (raw API response)
        const workoutPlan = plan.workoutPlan || plan.workout_plan;
        const dietPlan = plan.dietPlan || plan.diet_plan;
        const macroTargets = plan.macroTargets || plan.diet_macros;

        return (
            <View>
                <View style={styles.planCard}>
                    <View style={styles.planHeader}>
                        <Ionicons name="document-text" size={32} color="#3B82F6" />
                        <View style={{ marginLeft: 12 }}>
                            <Text style={styles.planTitle}>Your Customized Plan</Text>
                            <Text style={styles.planSubtitle}>Ready for review</Text>
                        </View>
                    </View>

                    <View style={styles.divider} />

                    <Text style={styles.sectionTitle}>Plan Overview</Text>
                    <Text style={styles.planDescription}>
                        Based on your profile, we've designed a comprehensive workout and nutrition strategy to help you reach your goals.
                    </Text>

                    {/* Macro Targets */}
                    {macroTargets && (
                        <View style={styles.macrosContainer}>
                            <Text style={styles.subsectionTitle}>Daily Nutrition Targets</Text>
                            <View style={styles.macrosGrid}>
                                <View style={styles.macroItem}>
                                    <Text style={styles.macroValue}>{macroTargets.calories}</Text>
                                    <Text style={styles.macroLabel}>Cal</Text>
                                </View>
                                <View style={styles.macroItem}>
                                    <Text style={styles.macroValue}>{macroTargets.protein}g</Text>
                                    <Text style={styles.macroLabel}>Protein</Text>
                                </View>
                                <View style={styles.macroItem}>
                                    <Text style={styles.macroValue}>{macroTargets.carbs}g</Text>
                                    <Text style={styles.macroLabel}>Carbs</Text>
                                </View>
                                <View style={styles.macroItem}>
                                    <Text style={styles.macroValue}>{macroTargets.fats}g</Text>
                                    <Text style={styles.macroLabel}>Fats</Text>
                                </View>
                            </View>
                        </View>
                    )}
                </View>

                {/* Workout Plan Summary */}
                {workoutPlan && workoutPlan.length > 0 && (
                    <View style={styles.planCard}>
                        <View style={styles.cardHeaderRow}>
                            <Ionicons name="barbell" size={24} color="#3B82F6" />
                            <Text style={styles.cardTitle}>Weekly Workout Schedule</Text>
                        </View>
                        <View style={styles.divider} />

                        {workoutPlan.map((dayPlan, index) => (
                            <View key={index} style={styles.dayRow}>
                                <View style={styles.dayInfo}>
                                    <Text style={styles.dayName}>{dayPlan.day}</Text>
                                    {dayPlan.session ? (
                                        <Text style={styles.sessionName}>{dayPlan.session}</Text>
                                    ) : (
                                        <Text style={styles.restDayText}>Rest Day</Text>
                                    )}
                                </View>
                                {dayPlan.exercises && dayPlan.exercises.length > 0 && (
                                    <View style={styles.badge}>
                                        <Text style={styles.badgeText}>{dayPlan.exercises.length} Exercises</Text>
                                    </View>
                                )}
                            </View>
                        ))}
                    </View>
                )}

                {/* Diet Plan Summary (Sample Day) */}
                {dietPlan && dietPlan.length > 0 && (
                    <View style={styles.planCard}>
                        <View style={styles.cardHeaderRow}>
                            <Ionicons name="restaurant" size={24} color="#10b981" />
                            <Text style={styles.cardTitle}>Sample Diet ({dietPlan[0].day})</Text>
                        </View>
                        <View style={styles.divider} />

                        {dietPlan[0].meals && dietPlan[0].meals.map((meal, index) => (
                            <View key={index} style={styles.mealRow}>
                                <View style={styles.mealTypeContainer}>
                                    <Text style={styles.mealType}>{meal.mealType}</Text>
                                </View>
                                <Text style={styles.mealName}>{meal.name}</Text>
                            </View>
                        ))}

                        <View style={styles.noteContainer}>
                            <Ionicons name="information-circle" size={16} color="#3B82F6" />
                            <Text style={styles.noteText}>
                                Full diet plan for all 7 days available in dashboard.
                            </Text>
                        </View>
                    </View>
                )}
            </View>
        );
    };

    return (
        <KeyboardAvoidingView
            style={[styles.container, { paddingTop: insets.top }]}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
            <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
                <Text style={styles.screenTitle}>Tentative Plan</Text>
                <Text style={styles.screenSubtitle}>Review your AI-generated plan</Text>

                {renderPlanSummary()}

                <View style={styles.feedbackSection}>
                    <Text style={styles.feedbackLabel}>Want to make changes?</Text>
                    <View style={styles.inputContainer}>
                        <TextInput
                            style={styles.input}
                            placeholderTextColor="#999"
                            placeholder="E.g., I prefer working out 4 days a week, or I don't like fish..."
                            value={refinementText}
                            onChangeText={setRefinementText}
                            multiline
                            textAlignVertical="top"
                        />
                    </View>
                    <TouchableOpacity
                        style={[styles.refineButton, loading && styles.disabledButton]}
                        onPress={handleRequestChanges}
                        disabled={loading}
                    >
                        {loading ? (
                            <ActivityIndicator color="white" size="small" />
                        ) : (
                            <Text style={styles.refineButtonText}>Regenerate Plan</Text>
                        )}
                    </TouchableOpacity>
                </View>

                <TouchableOpacity
                    style={[styles.confirmButton, loading && styles.disabledButton]}
                    onPress={handleConfirm}
                    disabled={loading}
                >
                    <Text style={styles.confirmButtonText}>Looks Good! Let's Start</Text>
                    <Ionicons name="arrow-forward" size={20} color="white" style={{ marginLeft: 8 }} />
                </TouchableOpacity>

                <Text style={styles.disclaimerText}>
                    * The plan can be edited later by contacting the admin.
                </Text>

                {/* Helper visual spacing */}
                <View style={{ height: 40 }} />
            </ScrollView>
        </KeyboardAvoidingView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#0B0E14',
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        padding: 20,
    },
    screenTitle: {
        fontSize: 28,
        fontWeight: 'bold',
        color: '#FFFFFF',
        marginBottom: 4,
    },
    screenSubtitle: {
        fontSize: 16,
        color: '#6D759E',
        marginBottom: 24,
    },
    planCard: {
        backgroundColor: '#161C2D',
        borderRadius: 16,
        padding: 20,
        shadowcolor: '#FFFFFF',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 3,
        marginBottom: 20,
    },
    planHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    cardHeaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
    },
    cardTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#FFFFFF',
        marginLeft: 10,
    },
    planTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#FFFFFF',
    },
    planSubtitle: {
        fontSize: 14,
        color: '#6D759E',
    },
    divider: {
        height: 1,
        backgroundColor: '#e5e7eb',
        marginVertical: 12,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: '#FFFFFF',
        marginBottom: 12,
    },
    subsectionTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: '#6D759E',
        marginBottom: 12,
        marginTop: 8,
    },
    planDescription: {
        fontSize: 14,
        color: '#6D759E',
        lineHeight: 22,
        marginBottom: 16,
    },
    macrosContainer: {
        backgroundColor: '#0B0E14',
        borderRadius: 12,
        padding: 12,
    },
    macrosGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    macroItem: {
        alignItems: 'center',
    },
    macroValue: {
        fontSize: 16,
        fontWeight: '700',
        color: '#3B82F6',
    },
    macroLabel: {
        fontSize: 12,
        color: '#6D759E',
    },
    dayRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#f3f4f6',
    },
    dayInfo: {
        flex: 1,
    },
    dayName: {
        fontSize: 15,
        fontWeight: '600',
        color: '#FFFFFF',
        marginBottom: 2,
    },
    sessionName: {
        fontSize: 13,
        color: '#6D759E',
    },
    restDayText: {
        fontSize: 13,
        color: '#6D759E',
        fontStyle: 'italic',
    },
    badge: {
        backgroundColor: '#eff6ff',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 12,
    },
    badgeText: {
        fontSize: 12,
        color: '#3B82F6',
        fontWeight: '500',
    },
    mealRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#f3f4f6',
    },
    mealTypeContainer: {
        width: 80,
    },
    mealType: {
        fontSize: 12,
        fontWeight: '700',
        color: '#10b981',
        textTransform: 'uppercase',
    },
    mealName: {
        fontSize: 14,
        color: '#FFFFFF',
        flex: 1,
    },
    noteContainer: {
        backgroundColor: '#0B0E14',
        padding: 12,
        borderRadius: 8,
        flexDirection: 'row',
        marginTop: 16,
        alignItems: 'center',
    },
    noteText: {
        fontSize: 13,
        color: '#6D759E',
        marginLeft: 8,
        flex: 1,
        lineHeight: 18,
    },
    feedbackSection: {
        marginBottom: 24,
    },
    feedbackLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: '#FFFFFF',
        marginBottom: 12,
    },
    inputContainer: {
        backgroundColor: '#161C2D',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#2C365E',
        marginBottom: 16,
    },
    input: {
        padding: 16,
        minHeight: 100,
        fontSize: 15,
        color: '#FFFFFF',
    },
    refineButton: {
        backgroundcolor: '#6D759E',
        paddingVertical: 14,
        borderRadius: 12,
        alignItems: 'center',
    },
    refineButtonText: {
        color: 'white',
        fontSize: 16,
        fontWeight: '600',
    },
    confirmButton: {
        backgroundColor: '#3B82F6', // Brand primary color
        paddingVertical: 18,
        borderRadius: 12,
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
        shadowColor: '#3B82F6',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 4,
    },
    confirmButtonText: {
        color: 'white',
        fontSize: 18,
        fontWeight: '700',
    },
    disabledButton: {
        opacity: 0.6,
    },
    disclaimerText: {
        marginTop: 24,
        textAlign: 'center',
        color: '#6D759E',
        fontSize: 13,
        fontStyle: 'italic',
    },
    placeholderText: {
        textAlign: 'center',
        color: '#6D759E',
        fontSize: 16,
        padding: 20
    }
});

export default TentativePlanScreen;
